import { WebViewController, WKWebViewController, BaseController } from '../controllers'
import { JSBridgeCore } from '../core/JSBridgeCore'
import { isRealseeVRWebview, isAndroid, isIOS } from '../utils/clientDetector'
import { DeviceInfo } from '../typings/DeviceInfo'
import { JSBridgeOptions } from '../typings/JSBridgeOptions'
import { JSBridgeReturnType } from '../typings/JSBridgeProtocol'
import { OpenWebViewEnum } from '../typings/OpenWebViewEnum'
import { ShareConfig } from '../typings/ShareConfig'
import { UserInfo } from '../typings/UserInfo'
import { WebViewState, WebViewErrorInfo, OrientationEnum } from '../typings/WebViewState'
import { WebViewTypeEnum } from '../typings/WebViewTypeEnum'
import { getUserAgent } from '../utils/clientDetector'
import { StaticData } from '../typings/StaticData'

const defaultOptions: Partial<JSBridgeOptions> = {
  timeout: 6000,
}

/**
 * App 客户端 JSBridge。
 * iOS [WKWebView](https://developer.apple.com/documentation/webkit/wkwebview)
 * Android [WebView](https://developer.android.google.cn/guide/webapps/webview)
 */
export class JSBridgeApp extends JSBridgeCore {
  options: JSBridgeOptions

  webViewType: WebViewTypeEnum

  private _controller: BaseController

  constructor(options: Partial<JSBridgeOptions> = {}) {
    super()

    this.options = Object.assign(defaultOptions, options)

    if (!isRealseeVRWebview) {
      console.warn('未知容器：此版本 `jsBridge` 不支持当前 WebView 容器 ... ')
    }

    this.webViewType = (() => {
      if (isAndroid()) return WebViewTypeEnum.WebView
      if (isIOS()) return WebViewTypeEnum.WKWebview
      return WebViewTypeEnum.Browser
    })()

    this._controller = (() => {
      if (isAndroid()) return new WebViewController()
      if (isIOS()) return new WKWebViewController()
      return new BaseController()
    })()

    this.callAndListen<WebViewState>('common/webViewState', (webviewState: WebViewState) => {
      this.emit('webViewStateChange', webviewState)
    })
    this.callAndListen<WebViewErrorInfo>('common/webViewError', (errorInfo: WebViewErrorInfo) => {
      this.emit('error', errorInfo)
    })
  }

  async ready() {
    return await this._controller.checkReady(this.options.timeout)
  }

  async callAndBackfeed<T>(schemeUrl: string) {
    return await this._controller.callAndBackfeed<T>(schemeUrl)
  }

  async callAndListen<T>(schemeUrl: string, callback: (data: T) => void) {
    return await this._controller?.callAndListen<T>(schemeUrl, callback)
  }

  async setLeftTopBackButton(enable?: boolean) {
    if (this.webViewType === WebViewTypeEnum.WebView) {
      return [true, null] as JSBridgeReturnType<boolean>
    }

    return await this.callAndBackfeed<boolean>(`common/setLeftTopBackButton?enable=${enable ? 1 : 0}`)
  }

  async getBangsHeight() {
    return await this.callAndBackfeed<number>('common/getBangsHeight')
  }

  async shock(duration = 4) {
    return await this.callAndBackfeed<boolean>(`common/shock?duration=${duration}`)
  }

  async minimize() {
    return await this.callAndBackfeed<boolean>(`common/minimize`)
  }

  async loadProgress(progress: number) {
    progress = progress < 0 ? 0 : progress > 100 ? 100 : progress
    return await this.callAndBackfeed<boolean>(`common/loadProgress?progress=${progress}`)
  }

  async closeLoading() {
    return await this.callAndBackfeed<boolean>(`common/closeLoading`)
  }

  async setOrientation(orientation: OrientationEnum) {
    return await this.callAndBackfeed<boolean>(`common/setOrientation?orientation=${orientation}`)
  }

  async keepScreenLight(enable: boolean) {
    return await this.callAndBackfeed<boolean>(`common/keepScreenLight?enable=${enable ? 1 : 0}`)
  }

  async closeWebView() {
    return await this.callAndBackfeed<boolean>(`common/closeWebView`)
  }

  async openWebView(url: string, way: OpenWebViewEnum) {
    return await this.callAndBackfeed<boolean>(`common/openWebView?way=${way}&url=${encodeURIComponent(url)}`)
  }

  async saveImage2Album(base64: string) {
    return await this.callAndBackfeed<boolean>(`common/saveImage2Album?base64=${base64}`)
  }

  async actionShare(shareConfig: Partial<ShareConfig>) {
    return await this.callAndBackfeed<boolean>(`common/actionShare?json=${JSON.stringify(shareConfig)}`)
  }

  async getDeviceInfo() {
    return await this.callAndBackfeed<DeviceInfo>(`common/getDeviceInfo`)
  }

  async getStaticData(): Promise<JSBridgeReturnType<StaticData | false>> {
    return await this._controller.getStaticData(this.options.timeout)
  }

  async getUserInfo() {
    return await this.callAndBackfeed<UserInfo>(`common/getUserInfo`)
  }

  async getWebViewState() {
    return await this.callAndBackfeed<WebViewState>(`common/getWebViewState`)
  }
  async login() {
    const res = await this.callAndBackfeed<UserInfo>(`common/login`)

    if (res[0]) {
      return await this.getUserInfo()
    }

    return res
  }

  async logout() {
    return await this.callAndBackfeed<boolean>(`common/logout`)
  }

  async detectMicro() {
    return await this.callAndBackfeed<boolean>(`common/detectMicro`)
  }

  async preload(urls: string[]) {
    urls = urls.map((url) => {
      if (url.startsWith('//')) {
        return 'https:' + url
      }

      return url
    })
    return await this.callAndBackfeed<boolean>(`common/preload?urls=${JSON.stringify(urls)}`)
  }

  createCacheProxyUrl(urls: string | string[]) {
    urls = Array.isArray(urls) ? urls : [urls]
    const userAgent = getUserAgent()

    // 不支持缓存代理
    if (!/supportCache/i.test(userAgent)) {
      return urls
    }

    const proxyCache = (url: string) => {
      if (this.webViewType === WebViewTypeEnum.WKWebview) {
        return url.replace(/https*:/, '').replace(/\/\//, 'realseeproxycache://')
      } else if (this.webViewType === WebViewTypeEnum.WebView) {
        return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'realseeproxycache=1'
      }
      return url
    }

    return urls.map(proxyCache)
  }
}
